import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaBars, FaTimes, FaUser } from "react-icons/fa";
import { apiUrl, brands } from "../../../config/config";
import SearchBar from "./SearchBar";
import CartCount from "./CartCount";
import logo from "../../../assets/blankbeanieslogo.png";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);

  const [megaMenuLeft, setMegaMenuLeft] = useState(0);
  const [megaMenuTop, setMegaMenuTop] = useState(0);
  const [megaMenuWidth, setMegaMenuWidth] = useState(260);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  const activeCategory = categories.find((cat) => cat.id === activeMenu);

  const activeCategoryProducts = activeMenu
    ? products.filter((p) => p.category_id === activeMenu).slice(0, 8)
    : [];

  const iconButtonClass =
    "flex h-10 w-10 items-center justify-center rounded-full bg-[#e5e8e6] text-[#1e242a] shadow-sm ring-1 ring-[#1e242a]/10 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1e242a] hover:text-white hover:shadow-md";

  const accountButtonClass =
    "flex h-10 items-center gap-2 rounded-full bg-[#e5e8e6] px-3 text-[#1e242a] shadow-sm ring-1 ring-[#1e242a]/10 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1e242a] hover:text-white hover:shadow-md";

  const getMegaMenuWidth = (count) => {
    if (count <= 1) return 245;
    if (count === 2) return 490;
    if (count === 3) return 735;
    return 980;
  };

  const handleMenuEnter = (catId, event) => {
    const currentProducts = products
      .filter((p) => p.category_id === catId)
      .slice(0, 8);

    const itemRect = event.currentTarget.getBoundingClientRect();
    const itemCenter = itemRect.left + itemRect.width / 2;

    const menuWidth = getMegaMenuWidth(currentProducts.length);
    const maxAllowedWidth = window.innerWidth - 32;
    const finalWidth = Math.min(menuWidth, maxAllowedWidth);

    const sideGap = 16;
    const halfMenu = finalWidth / 2;

    const minLeft = sideGap + halfMenu;
    const maxLeft = window.innerWidth - sideGap - halfMenu;

    const safeLeft = Math.min(Math.max(itemCenter, minLeft), maxLeft);

    setMegaMenuLeft(safeLeft);
    setMegaMenuTop(itemRect.bottom);
    setMegaMenuWidth(finalWidth);
    setActiveMenu(catId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));

    if (storedUser?.id) {
      fetch(`${apiUrl}/api/users/${storedUser.id}`)
        .then((res) => res.json())
        .then((data) => setUser(data))
        .catch(() => {});
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    window.location.href = "/login";
  };

  useEffect(() => {
    fetch(`${apiUrl}/api/${brands}/category`)
      .then((res) => res.json())
      .then((data) => setCategories(data.data || []))
      .catch((err) => console.log("Category fetch error:", err));
  }, []);

  useEffect(() => {
    fetch(`${apiUrl}/api/${brands}/products`)
      .then((res) => res.json())
      .then((data) => setProducts(data.data || []))
      .catch((err) => console.log("Products fetch error:", err));
  }, []);

  return (
    <>
      {/* ---------------- MAIN HEADER ---------------- */}
      <header className="sticky top-0 z-50 w-full border-b border-[#1e242a]/10 bg-white shadow-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="grid min-h-[82px] grid-cols-3 items-center sm:min-h-[92px] lg:min-h-[112px]">
            {/* LEFT SEARCH DESKTOP */}
            <div className="hidden justify-start lg:flex">
              <div className="w-full max-w-[430px]">
                <SearchBar />
              </div>
            </div>

            {/* MOBILE MENU */}
            <div className="flex justify-start lg:hidden">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className={iconButtonClass}
                aria-label="Open menu"
              >
                <FaBars className="text-[20px]" />
              </button>
            </div>

            {/* CENTER LOGO */}
            <div className="flex justify-center">
              <Link to="/" className="inline-flex items-center justify-center">
                <img
                  src={logo}
                  alt="Blank Beanies"
                  className="w-28 object-contain sm:w-36 lg:w-44"
                />
              </Link>
            </div>

            {/* RIGHT ACTIONS */}
            <div className="flex items-center justify-end gap-2 sm:gap-4 lg:gap-5">
              {/* MOBILE SEARCH */}
              <button
                type="button"
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className={`${iconButtonClass} lg:hidden`}
                aria-label="Search"
              >
                <FaSearch className="text-[18px]" />
              </button>

              {/* ACCOUNT */}
              <div className="relative" ref={dropdownRef}>
                {user ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className={accountButtonClass}
                      aria-label="Account"
                    >
                      <FaUser className="text-[18px]" />
                      <span className="hidden max-w-[120px] truncate text-[15px] font-semibold sm:inline">
                        {user.name}
                      </span>
                    </button>

                    {dropdownOpen && (
                      <div className="absolute right-0 z-50 mt-4 w-44 overflow-hidden rounded-xl border border-[#1e242a]/10 bg-white shadow-xl">
                        <p className="truncate border-b border-[#1e242a]/10 px-4 py-3 text-sm font-semibold text-[#1e242a]">
                          {user.name}
                        </p>

                        <Link
                          to="/account"
                          onClick={() => setDropdownOpen(false)}
                          className="block px-4 py-3 text-sm text-[#5f6870] transition-colors duration-300 hover:bg-[#f5f6f5] hover:text-[#1e242a]"
                        >
                          My Account
                        </Link>

                        <button
                          type="button"
                          onClick={handleLogout}
                          className="w-full px-4 py-3 text-left text-sm text-red-600 transition-colors duration-300 hover:bg-red-50"
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className={accountButtonClass}
                    aria-label="Login"
                  >
                    <FaUser className="text-[18px]" />
                    <span className="hidden text-[15px] font-semibold sm:inline">
                      Sign In
                    </span>
                  </Link>
                )}
              </div>

              {/* CART COMPONENT */}
             
                <CartCount />
              
            </div>
          </div>

          {/* MOBILE SEARCH BAR */}
          {showMobileSearch && (
            <div className="pb-4 lg:hidden">
              <SearchBar mobile />
            </div>
          )}
        </div>
      </header>

      {/* ---------------- CATEGORY NAVIGATION ---------------- */}
      <nav
        className="relative z-40 hidden w-full border-b border-white/10 bg-[#1e242a] shadow-sm lg:block"
        onMouseLeave={() => setActiveMenu(null)}
      >
        <div className="w-full px-6">
          <ul className="flex items-center justify-center gap-7 whitespace-nowrap">
            {categories.map((cat) => (
              <li
                key={cat.id}
                className="cursor-pointer py-3 font-medium"
                onMouseEnter={(event) => handleMenuEnter(cat.id, event)}
              >
                <Link
                  to={`/product/${cat.category_name.replace(/\s+/g, "-")}/${cat.id}`}
                  className="group relative inline-flex items-center justify-center text-[14px] font-semibold tracking-wide text-white/90 transition-all duration-300 ease-out hover:text-[#e5e8e6]"
                >
                  {cat.category_name}

                  <span
                    className={`absolute -bottom-3 left-1/2 h-[2px] -translate-x-1/2 rounded-full bg-[#e5e8e6] transition-all duration-300 ease-out ${
                      activeMenu === cat.id
                        ? "w-full opacity-100"
                        : "w-0 opacity-0 group-hover:w-full group-hover:opacity-100"
                    }`}
                  ></span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* ---------------- DESKTOP MEGA MENU ---------------- */}
        {activeCategory && activeCategoryProducts.length > 0 && (
          <div
            className="fixed z-[999] pt-3"
            style={{
              top: `${megaMenuTop}px`,
              left: `${megaMenuLeft}px`,
              transform: "translateX(-50%)",
            }}
            onMouseEnter={() => setActiveMenu(activeCategory.id)}
            onMouseLeave={() => setActiveMenu(null)}
          >
            <div
              className="overflow-visible rounded-2xl border border-[#1e242a]/10 bg-white p-5 shadow-2xl"
              style={{
                width: `${megaMenuWidth}px`,
                maxWidth: "calc(100vw - 32px)",
              }}
            >
              <div
                className="grid items-stretch justify-center gap-5"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(
                    activeCategoryProducts.length,
                    4
                  )}, minmax(205px, 230px))`,
                }}
              >
                {activeCategoryProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${activeCategory.category_name.replace(
                      /\s+/g,
                      "-"
                    )}/${activeCategory.id}/${product.name.replace(/\s+/g, "-")}/${
                      product.id
                    }`}
                    className="group block w-full min-w-0 rounded-2xl border border-[#1e242a]/10 bg-white p-3 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="flex h-[145px] items-center justify-center overflow-hidden rounded-xl bg-[#e5e8e6] sm:h-[155px] md:h-[165px]">
                      <img
                        src={`${apiUrl}/storage/${product.image}`}
                        alt={product.name}
                        className="max-h-[120px] max-w-[145px] object-contain transition-transform duration-300 group-hover:scale-105 sm:max-h-[130px]"
                      />
                    </div>

                    <h3 className="mt-4 min-h-[44px] break-words text-center text-sm font-bold leading-snug text-[#1e242a] sm:text-[15px]">
                      {product.name}
                    </h3>

                    <p className="mt-2 text-center text-sm font-semibold text-[#37404a]">
                      As low as ${product.basePrice || product.discountedPrice}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ---------------- MOBILE DRAWER ---------------- */}
      {open && (
        <div className="fixed inset-0 z-[999] bg-black/50 lg:hidden">
          <div className="fixed left-0 top-0 h-full w-[82%] max-w-[330px] overflow-y-auto bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#1e242a]/10 px-5 py-5">
              <Link to="/" onClick={() => setOpen(false)}>
                <img
                  src={logo}
                  alt="Blank Beanies"
                  className="w-28 object-contain"
                />
              </Link>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className={iconButtonClass}
                aria-label="Close menu"
              >
                <FaTimes className="text-[20px]" />
              </button>
            </div>

            <nav className="space-y-2 border-b border-[#1e242a]/10 px-5 py-5">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="block rounded-xl px-3 py-3 text-sm font-semibold text-[#1e242a] transition-colors duration-300 hover:bg-[#e5e8e6]"
              >
                Home
              </Link>

              <Link
                to="/products"
                onClick={() => setOpen(false)}
                className="block rounded-xl px-3 py-3 text-sm font-semibold text-[#1e242a] transition-colors duration-300 hover:bg-[#e5e8e6]"
              >
                Shop
              </Link>

              <Link
                to="/about-us"
                onClick={() => setOpen(false)}
                className="block rounded-xl px-3 py-3 text-sm font-semibold text-[#1e242a] transition-colors duration-300 hover:bg-[#e5e8e6]"
              >
                About
              </Link>

              <Link
                to="/contact-us"
                onClick={() => setOpen(false)}
                className="block rounded-xl px-3 py-3 text-sm font-semibold text-[#1e242a] transition-colors duration-300 hover:bg-[#e5e8e6]"
              >
                Contact
              </Link>

              {!user ? (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="block rounded-xl bg-[#1e242a] px-3 py-3 text-center text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#37404a]"
                >
                  Sign In
                </Link>
              ) : (
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  className="block rounded-xl bg-[#1e242a] px-3 py-3 text-center text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#37404a]"
                >
                  My Account
                </Link>
              )}
            </nav>

            {/* MOBILE CATEGORIES */}
            <div className="space-y-3 px-5 py-5">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="overflow-hidden rounded-xl border border-[#1e242a]/10 bg-white"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between bg-[#f5f6f5] px-4 py-3 transition-colors duration-300 hover:bg-[#e5e8e6]"
                    onClick={() =>
                      setMobileOpenMenu(mobileOpenMenu === cat.id ? null : cat.id)
                    }
                  >
                    <span className="text-sm font-semibold text-[#1e242a]">
                      {cat.category_name}
                    </span>

                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1e242a] text-lg font-bold text-white">
                      {mobileOpenMenu === cat.id ? "−" : "+"}
                    </span>
                  </button>

                  {mobileOpenMenu === cat.id && (
                    <div className="space-y-2 bg-white px-3 py-3">
                      <Link
                        to={`/product/${cat.category_name.replace(/\s+/g, "-")}/${cat.id}`}
                        onClick={() => setOpen(false)}
                        className="block rounded-lg px-3 py-2 text-sm font-semibold text-[#1e242a] transition-colors duration-300 hover:bg-[#e5e8e6]"
                      >
                        View All {cat.category_name}
                      </Link>

                      {products
                        .filter((p) => p.category_id === cat.id)
                        .slice(0, 8)
                        .map((product) => (
                          <Link
                            key={product.id}
                            to={`/product/${cat.category_name.replace(/\s+/g, "-")}/${cat.id}/${product.name.replace(/\s+/g, "-")}/${product.id}`}
                            onClick={() => setOpen(false)}
                            className="flex items-start gap-3 rounded-lg border border-[#1e242a]/10 p-2 transition-colors duration-300 hover:bg-[#f5f6f5]"
                          >
                            <img
                              src={`${apiUrl}/storage/${product.image}`}
                              className="h-16 w-16 flex-shrink-0 rounded-md object-contain"
                              alt={product.name}
                            />

                            <div className="min-w-0 flex-1">
                              <p className="break-words text-sm font-medium text-[#1e242a]">
                                {product.name}
                              </p>

                              <p className="mt-1 text-xs font-semibold text-[#37404a]">
                                ${product.discountedPrice}
                              </p>
                            </div>
                          </Link>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;